import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WEEKDAY_LABELS, type ShiftPositionInput, type ShiftSettings } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './shifts.css';

/** „37,5“ → 37.5, leer → null, Unsinn → NaN */
const num = (text: string): number | null => {
  const t = text.trim().replace(',', '.');
  if (!t) return null;
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : NaN;
};
const show = (n: number | null) => (n === null ? '' : String(n).replace('.', ','));

interface PositionRow {
  position: string;
  hours: string;
  period: 'week' | 'month';
}

/** Bereich „Schichtplanung“ der Einrichtung: Arbeitstage, Beginn, Grenzen und Sollstunden je Position (KON-040). Die Rechte prüft der Server. */
export function ShiftSettingsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('shifts.write');
  const settings = useQuery({ queryKey: ['shift-settings'], queryFn: async () => (await api<{ data: ShiftSettings }>('GET', '/shifts/settings')).data });

  const [workDays, setWorkDays] = useState<number[]>([]);
  const [start, setStart] = useState('08:00');
  const [maxDaily, setMaxDaily] = useState('');
  const [minRest, setMinRest] = useState('');
  const [maxVacation, setMaxVacation] = useState('');
  const [rows, setRows] = useState<PositionRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    setWorkDays(s.workDays);
    setStart(s.defaultStart);
    setMaxDaily(show(s.maxDailyHours));
    setMinRest(show(s.minRestHours));
    setMaxVacation(show(s.maxVacationDaysPerMonth));
    setRows(s.positions.map((p) => ({ position: p.position, hours: show(p.hours), period: p.period })));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => {
      const positions: ShiftPositionInput[] = rows.filter((r) => r.position.trim() || r.hours.trim()).map((r) => ({ position: r.position.trim(), hours: num(r.hours) ?? Number.NaN, period: r.period }));
      return api<{ data: ShiftSettings }>('PUT', '/shifts/settings', {
        workDays,
        defaultStart: start,
        maxDailyHours: num(maxDaily) ?? Number.NaN,
        minRestHours: num(minRest) ?? Number.NaN,
        maxVacationDaysPerMonth: num(maxVacation),
        positions,
      });
    },
    onSuccess: (res) => {
      setErrors({});
      setSaved(true);
      client.setQueryData(['shift-settings'], res.data);
      client.invalidateQueries({ queryKey: ['shift-employees'] });
    },
    onError: (e) => {
      setSaved(false);
      setErrors(e instanceof ApiError ? (e.status === 422 ? e.fields : { form: e.message }) : { form: 'Die Einrichtung konnte nicht gespeichert werden.' });
    },
  });

  const toggleDay = (d: number) => setWorkDays((list) => (list.includes(d) ? list.filter((x) => x !== d) : [...list, d].sort((a, b) => a - b)));
  const setRow = (i: number, patch: Partial<PositionRow>) => setRows((list) => list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const unused = (settings.data?.knownPositions ?? []).filter((p) => !rows.some((r) => r.position.toLowerCase() === p.toLowerCase()));

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    setErrors({});
    save.mutate();
  }

  if (settings.isLoading) return <section className="card"><p className="muted">Wird geladen …</p></section>;
  if (settings.isError) return <section className="card"><div className="alert alert--error">{(settings.error as Error).message}</div></section>;

  return (
    <section className="card">
      <h2>Schichtplanung</h2>
      <p className="muted small">Diese Vorgaben gelten für alle Entwürfe. Die Wochenstunden eines Mitarbeiters stehen in seinen Mitarbeiterdaten. Fehlen sie dort, gilt die Vorgabe seiner Position.</p>

      <form onSubmit={submit} noValidate>
        {errors.form && <div className="alert alert--error" role="alert">{errors.form}</div>}
        {saved && <div className="alert alert--ok" role="status">Gespeichert.</div>}

        <fieldset className="field-card" disabled={!canWrite}>
          <legend>Arbeitstage</legend>
          <div className="shift-days">
            {WEEKDAY_LABELS.map((label, d) => (
              <label key={label} className="chip">
                <input type="checkbox" checked={workDays.includes(d)} onChange={() => toggleDay(d)} /> {label}
              </label>
            ))}
          </div>
          {errors.workDays && <small className="field__error" role="alert">{errors.workDays}</small>}
          <small className="muted">Auf diese Tage verteilen sich die Wochenstunden. Für Mehrschichtbetrieb folgt ein eigenes Modell.</small>
        </fieldset>

        <div className="form-grid">
          <label className="field">
            <span>Beginn der Schicht</span>
            <input className="input" type="time" value={start} onChange={(e) => setStart(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.defaultStart} />
            {errors.defaultStart && <small className="field__error" role="alert">{errors.defaultStart}</small>}
          </label>
          <label className="field">
            <span>Höchstarbeitszeit am Tag (Stunden)</span>
            <input className="input" inputMode="decimal" value={maxDaily} onChange={(e) => setMaxDaily(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.maxDailyHours} />
            {errors.maxDailyHours ? <small className="field__error" role="alert">{errors.maxDailyHours}</small> : <small className="muted">Nach dem Arbeitszeitgesetz höchstens 10.</small>}
          </label>
          <label className="field">
            <span>Mindestruhezeit zwischen Schichten (Stunden)</span>
            <input className="input" inputMode="decimal" value={minRest} onChange={(e) => setMinRest(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.minRestHours} />
            {errors.minRestHours ? <small className="field__error" role="alert">{errors.minRestHours}</small> : <small className="muted">Nach dem Arbeitszeitgesetz mindestens 11.</small>}
          </label>
          <label className="field">
            <span>Höchstens Urlaubstage je Monat</span>
            <input className="input" inputMode="decimal" value={maxVacation} onChange={(e) => setMaxVacation(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.maxVacationDaysPerMonth} />
            {errors.maxVacationDaysPerMonth ? <small className="field__error" role="alert">{errors.maxVacationDaysPerMonth}</small> : <small className="muted">Leer: keine Grenze. Wird sie überschritten, warnt der Entwurf.</small>}
          </label>
        </div>

        <fieldset className="field-card" disabled={!canWrite}>
          <legend>Arbeitsstunden je Position</legend>
          <small className="muted">Gilt für Mitarbeiter ohne eigene Wochenstunden. Monatsstunden rechnet Wirkia auf die Woche um.</small>
          {errors.positions && <small className="field__error" role="alert">{errors.positions}</small>}
          {rows.map((r, i) => (
            <div key={i} className="option-row">
              <input className="input" value={r.position} onChange={(e) => setRow(i, { position: e.target.value })} placeholder="Position, zum Beispiel Koch" maxLength={100} aria-label="Position" list="shift-known-positions" />
              <input className="input shift-hours" value={r.hours} onChange={(e) => setRow(i, { hours: e.target.value })} inputMode="decimal" placeholder="Stunden" aria-label="Stunden" />
              <select className="input shift-period" value={r.period} onChange={(e) => setRow(i, { period: e.target.value as 'week' | 'month' })} aria-label="Zeitraum">
                <option value="week">pro Woche</option>
                <option value="month">pro Monat</option>
              </select>
              <button type="button" className="btn btn--secondary btn--sm" aria-label={`Position ${r.position || i + 1} entfernen`} onClick={() => setRows((list) => list.filter((_, j) => j !== i))}>Entfernen</button>
            </div>
          ))}
          <datalist id="shift-known-positions">{unused.map((p) => <option key={p} value={p} />)}</datalist>
          <div className="field-card__foot">
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setRows((list) => [...list, { position: '', hours: '', period: 'week' }])}>Position hinzufügen</button>
          </div>
        </fieldset>

        {canWrite && <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>}
      </form>
    </section>
  );
}
